import React, { useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Loader2, Gift, ShieldCheck } from "lucide-react";
import { AdminAuthProvider, useAdminAuth } from "./AdminAuthContext";
import AdminLogin from "./AdminLogin";
import AdminLayout from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function ForceChangePassword() {
  const { changePassword, logout } = useAdminAuth();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Use at least 8 characters.");
    if (pw !== confirm) return toast.error("Passwords do not match.");
    setLoading(true);
    try {
      await changePassword(pw);
      toast.success("Password updated \uD83C\uDF89");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update password.");
    } finally { setLoading(false); }
  };

  return (
    <div className="grid min-h-screen place-items-center wp-hero-gradient px-4" data-testid="admin-force-change">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <span className="grid h-12 w-12 place-items-center rounded-full bg-wp-rose-strong text-white shadow-wp-soft"><ShieldCheck className="h-6 w-6" /></span>
          <h1 className="mt-3 font-display text-2xl">Set a new password</h1>
          <p className="text-sm text-wp-ink-muted">For security, please replace your temporary password before continuing.</p>
        </div>
        <form onSubmit={submit} className="space-y-4 rounded-wp-xl border border-border bg-card p-6 shadow-wp-card">
          <div>
            <Label className="mb-1.5 block text-sm">New password</Label>
            <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="rounded-wp-md" data-testid="admin-force-change-new" />
          </div>
          <div>
            <Label className="mb-1.5 block text-sm">Confirm password</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="rounded-wp-md" data-testid="admin-force-change-confirm" />
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-xl bg-wp-rose-strong py-5 text-white hover:brightness-105" data-testid="admin-force-change-submit">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Update password
          </Button>
          <button type="button" onClick={logout} className="w-full text-center text-xs text-wp-ink-muted hover:underline">Sign out</button>
        </form>
      </div>
    </div>
  );
}

function RequireAuth({ children }) {
  const { authed, loading, mustChange } = useAdminAuth();
  if (loading) return <div className="grid min-h-screen place-items-center wp-hero-gradient"><Loader2 className="h-8 w-8 animate-spin text-wp-rose-strong" /></div>;
  if (!authed) return <Navigate to="/admin/login" replace />;
  if (mustChange) return <ForceChangePassword />;
  return children;
}

export default function AdminApp() {
  return (
    <AdminAuthProvider>
      <Routes>
        <Route path="login" element={<AdminLogin />} />
        <Route path="/*" element={<RequireAuth><AdminLayout /></RequireAuth>} />
      </Routes>
    </AdminAuthProvider>
  );
}
